import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  CheckSquare,
  ChevronDown,
  ChevronUp,
  LineChart,
  ListChecks,
  Plus,
  RotateCcw,
  SlidersHorizontal,
  Square,
  X,
} from "lucide-react";
import { Button } from "@/components/ui/Button";
import { SearchableSelect } from "@/components/ui/SearchableSelect";
import { WeekSelect } from "@/components/ui/WeekSelect";
import { Spinner } from "@/components/ui/Spinner";
import { MultiSelect } from "@/components/ui/MultiSelect";
import { useAuthStore } from "@/store/auth";
import { EmptyState, SectionToolbar, TableShell, Th } from "../components/ui";
import { useGlobalKpiFacets, useGlobalKpiStatusMutation, useGlobalKpiTasks } from "../hooks";
import * as api from "../api";
import { currentMonday, monthLabel, weekLabel } from "../components/KpiBacklogTree";
import { fmtDate, isDirectorateKpiItem, kpiOriginHint } from "../utils";
import type { GlobalKpiFilters, GlobalKpiRow, GlobalKpiSummary } from "../types";

const PAGE_SIZES = [25, 50, 100, 200];
const CHIPS_MAX_H = 132;

// Server-side sort keys accepted by kpi-action-items/global-list/ (see kpi_global.list_ordering).
type SortKey = "due_date" | "project" | "assignee" | "status" | "created" | "urgency";
interface SortState {
  key: SortKey | null;
  dir: "asc" | "desc";
}

const FILTERS_STORAGE_KEY = "powerProjects.globalKpi.filters.v1";

// Domyślny widok: otwarte zadania. Zakres (moje projekty vs cały portfel) rozstrzyga
// backend — nie ma tu żadnego filtra „moje", bo nie da się go obejść z przeglądarki.
const defaultFilters = (): GlobalKpiFilters => ({ statuses: ["open"] });

interface StoredState {
  filters: GlobalKpiFilters;
  sort: SortState;
  pageSize: number;
}

function readStored(): StoredState | null {
  try {
    const raw = localStorage.getItem(FILTERS_STORAGE_KEY);
    return raw ? (JSON.parse(raw) as StoredState) : null;
  } catch {
    return null;
  }
}

function writeStored(state: StoredState) {
  try {
    localStorage.setItem(FILTERS_STORAGE_KEY, JSON.stringify(state));
  } catch {
    // ignore write failures (private mode / quota)
  }
}

// Filter keys → the chip label shown when the panel is collapsed.
const LIST_KEY_LABELS: Record<string, string> = {
  project_ids: "Przedsięwzięcie",
  coordinator_ids: "Koordynator",
  designer_ids: "Prowadzący ZE",
  client_ids: "Klient",
  branch_ids: "Oddział",
  assignee_ids: "Odpowiedzialny",
  statuses: "Status",
};
const SCALAR_KEY_LABELS: Record<string, string> = {
  due: "Termin",
  search: "Szukaj",
};

function countActiveFilters(f: GlobalKpiFilters): number {
  const fr = f as Record<string, unknown>;
  const lists = Object.keys(LIST_KEY_LABELS).filter((k) => (fr[k] as string[] | undefined)?.length);
  const scalars = Object.keys(SCALAR_KEY_LABELS).filter((k) =>
    ((fr[k] as string | undefined) ?? "").trim(),
  );
  return lists.length + scalars.length;
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block">
      <span className="section-label">{label}</span>
      <div className="mt-1">{children}</div>
    </label>
  );
}

function SortHeader({
  label,
  col,
  sort,
  onSort,
  className,
}: {
  label: string;
  col: SortKey;
  sort: SortState;
  onSort: (k: SortKey) => void;
  className?: string;
}) {
  const active = sort.key === col;
  return (
    <Th className={className}>
      <button
        type="button"
        onClick={() => onSort(col)}
        className="inline-flex items-center gap-1 uppercase tracking-widest hover:text-[var(--color-text-primary)] transition-colors"
        title="Sortuj"
      >
        {label}
        <span
          className={
            active ? "text-[var(--color-primary)]" : "text-[var(--color-text-muted)] opacity-40"
          }
        >
          {active ? (sort.dir === "asc" ? "▲" : "▼") : "↕"}
        </span>
      </button>
    </Th>
  );
}

function FilterChip({ label, onRemove }: { label: string; onRemove: () => void }) {
  return (
    <span className="inline-flex items-center gap-1 max-w-[240px] border border-[var(--color-border-strong)] bg-[var(--color-bg-surface)] px-2 py-0.5 text-[11px] font-mono text-[var(--color-text-secondary)]">
      <span className="truncate">{label}</span>
      <button
        type="button"
        onClick={onRemove}
        className="text-[var(--color-text-muted)] hover:text-[var(--color-primary)]"
        title="Usuń filtr"
      >
        <X size={11} />
      </button>
    </span>
  );
}

/* Kafelek liczbowy nad tabelą — klik ustawia odpowiadający mu filtr, więc liczba i
   zawartość tabeli zawsze się zgadzają. Liczniki liczą się z filtrów BEZ terminu. */
function Tile({
  label,
  value,
  active,
  tone,
  onClick,
}: {
  label: string;
  value: number;
  active: boolean;
  tone?: "danger" | "warn";
  onClick: () => void;
}) {
  const color =
    tone === "danger"
      ? "text-[var(--color-danger)]"
      : tone === "warn"
        ? "text-[var(--color-warning)]"
        : "text-[var(--color-text-primary)]";
  return (
    <button
      type="button"
      onClick={onClick}
      className={`card px-3 py-2 text-left min-w-[110px] transition-colors ${
        active
          ? "border-[var(--color-primary)]"
          : "hover:border-[var(--color-border-strong)]"
      }`}
    >
      <div className={`font-mono text-lg leading-none ${color}`}>{value}</div>
      <div className="section-label mt-1">{label}</div>
    </button>
  );
}

// Termin: data + ile dni zostało / minęło (puste = „do następnego KPI").
function DueCell({ row }: { row: GlobalKpiRow }) {
  if (!row.due_date) {
    return (
      <span className="text-[var(--color-text-muted)]" title="Termin: do następnego przeglądu KPI">
        do nast. KPI
      </span>
    );
  }
  const days = row.days_left;
  const suffix =
    days === null
      ? ""
      : days < 0
        ? `${Math.abs(days)} dni po`
        : days === 0
          ? "dziś"
          : `za ${days} dni`;
  return (
    <div className="whitespace-nowrap">
      <div className={row.overdue ? "text-[var(--color-danger)]" : ""}>{fmtDate(row.due_date)}</div>
      {suffix && (
        <div
          className={`text-[10px] font-mono ${
            row.overdue ? "text-[var(--color-danger)]" : "text-[var(--color-text-muted)]"
          }`}
        >
          {suffix}
        </div>
      )}
    </div>
  );
}

/**
 * Wiersze tabeli w trybie „Tygodniami": przed każdą grupą wchodzi wiersz-nagłówek
 * „SIERPIEŃ 2026 · TYDZIEŃ 2 · 10–16.08". Kolejność WEWNĄTRZ grupy zostaje taka, jaką dał
 * serwer, więc sortowanie kolumnami działa jak dotąd. Bez terminu zawsze na końcu.
 */
type TableEntry =
  | { kind: "header"; key: string; label: string; count: number }
  | { kind: "row"; row: GlobalKpiRow };

function groupByWeek(rows: GlobalKpiRow[]): TableEntry[] {
  const buckets = new Map<string, GlobalKpiRow[]>();
  for (const row of rows) {
    const key = row.week_start ?? "none";
    const list = buckets.get(key) ?? [];
    list.push(row);
    buckets.set(key, list);
  }
  const keys = [...buckets.keys()].sort((a, b) => {
    if (a === "none") return 1;
    if (b === "none") return -1;
    return a.localeCompare(b);
  });
  const today = currentMonday();
  const out: TableEntry[] = [];
  for (const key of keys) {
    const list = buckets.get(key)!;
    const label =
      key === "none"
        ? "Bez terminu — do następnego KPI"
        : `${monthLabel(key.slice(0, 7))} · ${weekLabel(key)}${key === today ? " · ten tydzień" : ""}`;
    out.push({ kind: "header", key, label, count: list.length });
    for (const row of list) out.push({ kind: "row", row });
  }
  return out;
}

/**
 * Dopisanie sobie zadania bez wchodzenia w przyłącze. Wybiera się przedsięwzięcie i
 * tydzień — spotkania KPI nie, bo backend sam podpina zadanie pod bieżący przegląd.
 */
function AddTaskModal({
  projectOptions,
  onClose,
}: {
  projectOptions: { value: string; label: string }[];
  onClose: (created: boolean) => void;
}) {
  const [projectId, setProjectId] = useState("");
  const [week, setWeek] = useState<string | null>(currentMonday());
  const [description, setDescription] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const submit = async () => {
    if (!projectId || !description.trim()) return;
    setBusy(true);
    setError("");
    try {
      // Przedsięwzięcie → profil ZE: filtry operują na przedsięwzięciach, a zadanie wisi
      // przy przyłączu, więc tłumaczymy jedno na drugie tuż przed zapisem.
      const connection = await api.getConnectionByProject(projectId);
      if (!connection) {
        setError("To przedsięwzięcie nie ma profilu projektu ZE — nie ma gdzie dodać zadania KPI.");
        return;
      }
      await api.createKpiActionItem({
        connection: connection.id,
        description: description.trim(),
        due_date: week,
      });
      onClose(true);
    } catch {
      setError("Nie udało się dodać zadania.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center bg-black/40 p-4 pt-24" onClick={() => onClose(false)}>
      <div className="card w-full max-w-lg p-4 space-y-3" onClick={(e) => e.stopPropagation()}>
        <h2 className="text-sm font-semibold">Nowe zadanie KPI</h2>
        <label className="block">
          <span className="section-label">Przedsięwzięcie</span>
          <div className="mt-1">
            <SearchableSelect
              options={projectOptions}
              value={projectId}
              placeholder="— wybierz —"
              onChange={(e) => setProjectId(e.target.value)}
            />
          </div>
        </label>
        <label className="block">
          <span className="section-label">Zadanie</span>
          <input
            className="input-base mt-1 w-full"
            autoFocus
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") submit(); }}
          />
        </label>
        <div>
          <span className="section-label">Tydzień realizacji</span>
          <div className="mt-1">
            <WeekSelect value={week} onChange={setWeek} placeholder="— do następnego KPI —" />
          </div>
        </div>
        {error && <p className="text-xs text-[var(--color-danger)]">{error}</p>}
        <div className="flex items-center gap-2 pt-1">
          <Button size="sm" disabled={!projectId || !description.trim() || busy} onClick={submit}>
            Dodaj
          </Button>
          <Button size="sm" variant="ghost" onClick={() => onClose(false)}>Anuluj</Button>
          <span className="text-[11px] font-mono text-[var(--color-text-muted)]">
            Zadanie trafi do bieżącego KPI wybranego przedsięwzięcia.
          </span>
        </div>
      </div>
    </div>
  );
}

function StatusBadge({ row }: { row: GlobalKpiRow }) {
  const cls =
    row.status === "done"
      ? "text-[var(--color-success)]"
      : row.status === "cancelled"
        ? "text-[var(--color-text-muted)] line-through"
        : row.overdue
          ? "text-[var(--color-danger)]"
          : "text-[var(--color-text-secondary)]";
  return <span className={`font-mono text-[11px] uppercase ${cls}`}>{row.status_display}</span>;
}

/* Zakładka „Zadania KPI" strony „Kontrola procesu" (/power-projects/kontrola?tab=kpi). */
export function GlobalKpiSection() {
  const me = useAuthStore((s) => s.user);

  const initial = useMemo<StoredState>(() => {
    const stored = readStored();
    return {
      filters: stored?.filters ?? defaultFilters(),
      sort: stored?.sort ?? { key: null, dir: "asc" },
      pageSize: stored?.pageSize ?? 50,
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [f, setF] = useState<GlobalKpiFilters>(initial.filters);
  const [sort, setSort] = useState<SortState>(initial.sort);
  const [pageSize, setPageSize] = useState<number>(initial.pageSize);
  const [page, setPage] = useState(1);
  const [panelOpen, setPanelOpen] = useState(false);
  // Grupowanie w miesiące/tygodnie — ten sam podział co w zakładce KPI przyłącza, tylko
  // w poprzek wszystkich przedsięwzięć. Sortowanie kolumnami działa dalej wewnątrz tygodnia.
  const [grouped, setGrouped] = useState(false);
  const [addOpen, setAddOpen] = useState(false);

  const upd = (patch: Partial<GlobalKpiFilters>) => setF((s) => ({ ...s, ...patch }));
  const filtersKey = JSON.stringify(f);
  const activeCount = useMemo(() => countActiveFilters(f), [filtersKey]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    writeStored({ filters: f, sort, pageSize });
  }, [filtersKey, sort, pageSize]); // eslint-disable-line react-hooks/exhaustive-deps

  // Debounce: typing in a search box shouldn't fire a request per keystroke.
  const [debounced, setDebounced] = useState<GlobalKpiFilters>(initial.filters);
  useEffect(() => {
    const id = setTimeout(() => {
      setDebounced(f);
      setPage(1);
    }, 350);
    return () => clearTimeout(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filtersKey]);

  // Facets: full list keeps chip labels stable; live list cascades with the other filters.
  // Modal „Dodaj zadanie" też potrzebuje listy przedsięwzięć, inaczej otwiera się pusty.
  const facetsNeeded = panelOpen || activeCount > 0 || addOpen;
  const { data: fullFacets, isLoading: facetsLoading } = useGlobalKpiFacets({}, facetsNeeded);
  const { data: liveFacets } = useGlobalKpiFacets(debounced, panelOpen);
  const facets = liveFacets ?? fullFacets;

  const entityLabels = useMemo(() => {
    const m = new Map<string, string>();
    if (fullFacets) {
      [
        ...fullFacets.projects,
        ...fullFacets.coordinators,
        ...fullFacets.designers,
        ...fullFacets.clients,
        ...fullFacets.branches,
        ...fullFacets.assignees,
      ].forEach((e) => m.set(e.id, e.name));
    }
    return m;
  }, [fullFacets]);

  const entityOptions = (list: { id: string; name: string }[] | undefined, selected: string[]) => {
    const opts = (list ?? []).map((e) => ({ value: e.id, label: e.name }));
    const present = new Set(opts.map((o) => o.value));
    selected.forEach((id) => {
      if (!present.has(id)) opts.push({ value: id, label: entityLabels.get(id) ?? id });
    });
    return opts;
  };

  const statusLabels = useMemo(() => {
    const m = new Map<string, string>();
    (fullFacets?.statuses ?? []).forEach((o) => m.set(o.value, o.label));
    return m;
  }, [fullFacets]);

  const dueLabels = useMemo(() => {
    const m = new Map<string, string>();
    (fullFacets?.due_options ?? []).forEach((o) => m.set(o.value, o.label));
    return m;
  }, [fullFacets]);

  const chips = useMemo(() => {
    const out: { key: string; label: string; remove: Partial<GlobalKpiFilters> }[] = [];
    const fr = f as Record<string, unknown>;
    for (const [key, label] of Object.entries(LIST_KEY_LABELS)) {
      const vals = fr[key] as string[] | undefined;
      if (!vals?.length) continue;
      const isMine = key === "assignee_ids" && vals.length === 1 && !!me?.id && vals[0] === me.id;
      const names = vals.map(
        (v) => (key === "statuses" ? statusLabels.get(v) : entityLabels.get(v)) ?? v,
      );
      out.push({
        key,
        label: isMine ? "Odpowiedzialny: ja" : `${label}: ${names.join(", ")}`,
        remove: { [key]: [] },
      });
    }
    const due = f.due?.trim();
    if (due) out.push({ key: "due", label: dueLabels.get(due) ?? due, remove: { due: "" } });
    return out;
  }, [filtersKey, entityLabels, statusLabels, dueLabels, me?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const ordering = sort.key ? `${sort.dir === "desc" ? "-" : ""}${sort.key}` : "";
  const list = useGlobalKpiTasks({ filters: debounced, page, page_size: pageSize, ordering });
  const data = list.data;
  const entries = useMemo<TableEntry[]>(() => {
    const rows = data?.results ?? [];
    return grouped ? groupByWeek(rows) : rows.map((row) => ({ kind: "row" as const, row }));
  }, [data, grouped]);
  const summary: GlobalKpiSummary | undefined = data?.summary;

  const setStatus = useGlobalKpiStatusMutation();

  const onSort = (k: SortKey) =>
    setSort((s) =>
      s.key === k ? { key: k, dir: s.dir === "asc" ? "desc" : "asc" } : { key: k, dir: "asc" },
    );

  // Kafelek przełącza swój filtr (drugi klik = zdejmuje go). Kafelki terminowe liczą
  // wyłącznie zadania do zrobienia, więc przypinają też status — inaczej liczba na
  // kafelku nie zgadzałaby się z tym, co po kliknięciu pokazuje tabela.
  const toggleDue = (value: string) =>
    upd(f.due === value ? { due: "" } : { due: value, statuses: ["open"] });
  const toggleStatus = (value: string) =>
    upd({ statuses: f.statuses?.length === 1 && f.statuses[0] === value ? [] : [value] });

  const onlyMine = () => upd({ assignee_ids: me?.id ? [me.id] : [] });
  const clearAll = () => setF({});

  return (
    <div>
      <div className="mb-3">
        <SectionToolbar
          text={
            data?.scope === "all"
              ? "Wszystkie zadania z przeglądów KPI — cały portfel projektów ZE."
              : "Zadania z przeglądów KPI Twoich projektów ZE — wszystkie przedsięwzięcia łącznie."
          }
        />
      </div>

      {addOpen && (
        <AddTaskModal
          projectOptions={(fullFacets?.projects ?? []).map((p) => ({ value: p.id, label: p.name }))}
          onClose={(created) => {
            setAddOpen(false);
            if (created) list.refetch();
          }}
        />
      )}

      {/* ── kafelki (klik = filtr) ── */}
      {summary && (
        <div className="flex flex-wrap gap-2 mb-3">
          <Tile
            label="Po terminie"
            value={summary.overdue}
            tone="danger"
            active={f.due === "overdue"}
            onClick={() => toggleDue("overdue")}
          />
          <Tile
            label="≤ 7 dni"
            value={summary.week}
            tone="warn"
            active={f.due === "week"}
            onClick={() => toggleDue("week")}
          />
          <Tile
            label="Bez terminu"
            value={summary.no_date}
            active={f.due === "none"}
            onClick={() => toggleDue("none")}
          />
          <Tile
            label="Nieukończone"
            value={summary.open}
            active={f.statuses?.length === 1 && f.statuses[0] === "open"}
            onClick={() => toggleStatus("open")}
          />
          <Tile
            label="Zrobione"
            value={summary.done}
            active={f.statuses?.length === 1 && f.statuses[0] === "done"}
            onClick={() => toggleStatus("done")}
          />
        </div>
      )}

      {/* ── toolbar (always visible) ── */}
      <div className="flex flex-wrap items-center gap-2 mb-2">
        <input
          placeholder="Szukaj: treść zadania, przedsięwzięcie, nr umowy…"
          className="input-base w-full sm:w-auto sm:flex-1 sm:min-w-[220px] sm:max-w-md"
          value={f.search ?? ""}
          onChange={(e) => upd({ search: e.target.value })}
        />
        <Button
          variant={panelOpen ? "secondary" : "ghost"}
          size="sm"
          onClick={() => setPanelOpen((o) => !o)}
        >
          <SlidersHorizontal size={13} />
          Filtry{activeCount > 0 ? ` (${activeCount})` : ""}
          {panelOpen ? <ChevronUp size={13} /> : <ChevronDown size={13} />}
        </Button>
        <Button variant="ghost" size="sm" onClick={onlyMine}>
          Moje zadania
        </Button>
        <Button
          variant={f.include_self ? "secondary" : "ghost"}
          size="sm"
          onClick={() => upd({ include_self: f.include_self ? undefined : true })}
          title="Domyślnie widać tylko ustalenia ze spotkań KPI. To dołącza zapiski dopisane przez pracowników w planach tygodniowych."
        >
          {f.include_self ? "Tylko ustalenia" : "Pokaż też własne"}
        </Button>
        <Button
          variant={grouped ? "secondary" : "ghost"}
          size="sm"
          onClick={() => setGrouped((v) => !v)}
          title="Pogrupuj zadania w miesiące i tygodnie — tak jak w zakładce KPI przyłącza"
        >
          {grouped ? "Lista" : "Tygodniami"}
        </Button>
        {data?.scope === "all" && (
          // Wejście do OSOBNEGO widoku dyrekcji — celowo nie „ghost" jak sąsiednie
          // przełączniki tej listy, bo to nie kolejny filtr, tylko inny ekran.
          <Link
            to="/power-projects/kpi/analiza"
            className="ml-1 inline-flex items-center gap-1.5 border border-[var(--color-text-primary)] bg-[var(--color-text-primary)] px-3 py-1.5 font-mono text-xs font-bold uppercase tracking-wider text-[var(--color-text-inverse)] shadow-hard-red transition-all duration-100 hover:-translate-y-px hover:bg-[var(--color-primary)] hover:border-[var(--color-primary)] hover:shadow-none focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--color-primary)]"
            title="Rozliczenie postępów wybranej osoby względem wybranej chwili — widok dyrekcji technicznej"
          >
            <LineChart size={13} />
            Analiza postępów
          </Link>
        )}
        <Button variant="ghost" size="sm" onClick={() => setAddOpen(true)}>
          <Plus size={13} />
          Dodaj zadanie
        </Button>
        {activeCount > 0 && (
          <Button variant="ghost" size="sm" onClick={clearAll}>
            Wyczyść filtry
          </Button>
        )}
        <span className="section-label ml-auto whitespace-nowrap">
          {list.isFetching && !data ? "…" : `${data?.count ?? 0} zadań`}
        </span>
      </div>

      {/* ── active-filter chips ── */}
      {chips.length > 0 && (
        <div className="flex flex-wrap items-center gap-1.5 mb-3">
          {chips.map((c) => (
            <FilterChip key={c.key} label={c.label} onRemove={() => upd(c.remove)} />
          ))}
        </div>
      )}

      {/* ── expandable filter panel ── */}
      {panelOpen && (
        <div className="card p-4 mb-4 space-y-4 text-sm">
          {facetsLoading ? (
            <div className="py-6 flex justify-center">
              <Spinner />
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
              <Field label="Przedsięwzięcie">
                <MultiSelect
                  options={entityOptions(facets?.projects, f.project_ids ?? [])}
                  value={f.project_ids ?? []}
                  onChange={(v) => upd({ project_ids: v })}
                  chipsMaxHeight={CHIPS_MAX_H}
                />
              </Field>
              <Field label="Osoba odpowiedzialna">
                <MultiSelect
                  options={entityOptions(facets?.assignees, f.assignee_ids ?? [])}
                  value={f.assignee_ids ?? []}
                  onChange={(v) => upd({ assignee_ids: v })}
                  placeholder="— wszyscy —"
                  chipsMaxHeight={CHIPS_MAX_H}
                />
              </Field>
              <Field label="Koordynator przedsięwzięcia">
                <MultiSelect
                  options={entityOptions(facets?.coordinators, f.coordinator_ids ?? [])}
                  value={f.coordinator_ids ?? []}
                  onChange={(v) => upd({ coordinator_ids: v })}
                  placeholder="— wszyscy —"
                  chipsMaxHeight={CHIPS_MAX_H}
                />
              </Field>
              <Field label="Prowadzący ZE">
                <MultiSelect
                  options={entityOptions(facets?.designers, f.designer_ids ?? [])}
                  value={f.designer_ids ?? []}
                  onChange={(v) => upd({ designer_ids: v })}
                  placeholder="— wszyscy —"
                  chipsMaxHeight={CHIPS_MAX_H}
                />
              </Field>
              <Field label="Klient">
                <MultiSelect
                  options={entityOptions(facets?.clients, f.client_ids ?? [])}
                  value={f.client_ids ?? []}
                  onChange={(v) => upd({ client_ids: v })}
                  placeholder="— wszyscy —"
                  chipsMaxHeight={CHIPS_MAX_H}
                />
              </Field>
              <Field label="Oddział">
                <MultiSelect
                  options={entityOptions(facets?.branches, f.branch_ids ?? [])}
                  value={f.branch_ids ?? []}
                  onChange={(v) => upd({ branch_ids: v })}
                  chipsMaxHeight={CHIPS_MAX_H}
                />
              </Field>
              <Field label="Status">
                <MultiSelect
                  options={(facets?.statuses ?? []).map((s) => ({
                    value: s.value,
                    label: s.label,
                  }))}
                  value={f.statuses ?? []}
                  onChange={(v) => upd({ statuses: v })}
                  chipsMaxHeight={CHIPS_MAX_H}
                />
              </Field>
              <Field label="Termin">
                <select
                  className="input-base w-full"
                  value={f.due ?? ""}
                  onChange={(e) => upd({ due: e.target.value })}
                >
                  <option value="">Termin: wszystkie</option>
                  {(facets?.due_options ?? []).map((o) => (
                    <option key={o.value} value={o.value}>
                      {o.label}
                    </option>
                  ))}
                </select>
              </Field>
            </div>
          )}
        </div>
      )}

      {/* ── wyniki ── */}
      {list.isLoading ? (
        <div className="py-12 flex justify-center">
          <Spinner />
        </div>
      ) : !data || data.count === 0 ? (
        <EmptyState>
          <ListChecks size={28} className="mx-auto mb-2 opacity-50" />
          <div>Brak zadań KPI dla wybranych filtrów</div>
          <div className="mt-1 text-[11px] normal-case tracking-normal">
            Zmień kryteria albo wyczyść filtry, aby zobaczyć wszystkie zadania.
          </div>
        </EmptyState>
      ) : (
        <>
          <TableShell>
            <thead>
              <tr>
                <Th className="w-8" />
                <Th>Zadanie</Th>
                <SortHeader label="Przedsięwzięcie" col="project" sort={sort} onSort={onSort} />
                <SortHeader label="Odpowiedzialny" col="assignee" sort={sort} onSort={onSort} />
                <SortHeader label="Termin" col="due_date" sort={sort} onSort={onSort} />
                <SortHeader label="Status" col="status" sort={sort} onSort={onSort} />
                <Th>KPI</Th>
                <Th>Klient / oddział</Th>
              </tr>
            </thead>
            <tbody>
              {entries.map((entry) => {
                if (entry.kind === "header") {
                  return (
                    <tr key={entry.key} className="bg-[var(--color-bg-elevated)]">
                      <td colSpan={8} className="px-3 py-1.5">
                        <span className="text-xs font-semibold uppercase tracking-wide text-[var(--color-text-secondary)]">
                          {entry.label}
                        </span>
                        <span className="ml-2 font-mono text-[11px] text-[var(--color-text-muted)]">
                          {entry.count} zad.
                        </span>
                      </td>
                    </tr>
                  );
                }
                const r = entry.row;
                const done = r.status === "done";
                const pending = setStatus.isPending && setStatus.variables?.id === r.id;
                // Ta sama reguła co w drzewie KPI: ustalenie dyrekcji na czerwono,
                // zapisek własny zwykłym tuszem (zgłoszenie „Wyszczególnienie opisu KPI").
                const directive = isDirectorateKpiItem(r);
                const descClass = done
                  ? directive
                    ? "text-[rgba(220,31,31,0.55)] line-through"
                    : "text-[var(--color-text-muted)] line-through"
                  : directive
                    ? "font-semibold text-[var(--color-primary)]"
                    : "";
                return (
                  <tr
                    key={r.id}
                    className="border-t border-[var(--color-border-muted)] hover:bg-[var(--color-bg-elevated)]"
                  >
                    <td className="px-2 py-2">
                      <button
                        type="button"
                        disabled={pending || r.status === "cancelled"}
                        onClick={() =>
                          setStatus.mutate({ id: r.id, status: done ? "open" : "done" })
                        }
                        className="text-[var(--color-text-muted)] hover:text-[var(--color-primary)] disabled:opacity-40"
                        title={done ? "Cofnij do zrobienia" : "Odhacz jako zrobione"}
                      >
                        {done ? (
                          <CheckSquare size={15} className="text-[var(--color-success)]" />
                        ) : r.status === "cancelled" ? (
                          <RotateCcw size={15} />
                        ) : (
                          <Square size={15} />
                        )}
                      </button>
                    </td>
                    <td className="px-3 py-2 max-w-[380px]">
                      {r.parent_path && (
                        <div className="text-[10px] font-mono text-[var(--color-text-muted)] truncate" title={r.parent_path}>
                          {r.parent_path} →
                        </div>
                      )}
                      <div className={descClass} title={kpiOriginHint(r)}>
                        {directive ? (
                          <span
                            className={`mr-1.5 rounded-sm border border-[var(--color-primary)] px-1 font-mono text-[10px] font-semibold ${
                              done
                                ? "text-[var(--color-primary)] opacity-60"
                                : "bg-[var(--color-primary)] text-white"
                            }`}
                          >
                            {r.origin === "kpi" ? `KPI #${r.review_sequence_no}` : "dyrekcja"}
                          </span>
                        ) : (
                          <span className="mr-1.5 rounded-sm border border-gray-200 px-1 font-mono text-[10px] text-gray-400 dark:border-gray-600">
                            własne
                          </span>
                        )}
                        {r.description}
                      </div>
                      {done && r.done_at && (
                        <div className="text-[10px] font-mono text-[var(--color-text-muted)]">
                          zrobione {fmtDate(r.done_at)}
                          {r.done_by_name ? ` · ${r.done_by_name}` : ""}
                        </div>
                      )}
                    </td>
                    <td className="px-3 py-2 max-w-[220px]">
                      <Link
                        to={`/power-projects/${r.project_id}?tab=kpi`}
                        className="text-[var(--color-primary)] hover:underline block truncate"
                        title={[
                          r.project_name,
                          r.contract_number && `Nr umowy: ${r.contract_number}`,
                          r.coordinators && `Koordynator: ${r.coordinators}`,
                          r.designer_name && `Prowadzący ZE: ${r.designer_name}`,
                        ]
                          .filter(Boolean)
                          .join("\n")}
                      >
                        {r.project_name}
                      </Link>
                    </td>
                    <td className="px-3 py-2 whitespace-nowrap">
                      {r.assignee_name || (
                        <span className="text-[var(--color-text-muted)]">— nieprzypisane —</span>
                      )}
                    </td>
                    <td className="px-3 py-2">
                      <DueCell row={r} />
                    </td>
                    <td className="px-3 py-2 whitespace-nowrap">
                      <StatusBadge row={r} />
                    </td>
                    <td
                      className="px-3 py-2 whitespace-nowrap font-mono text-[11px] text-[var(--color-text-muted)]"
                      title={`Zadanie z przeglądu KPI #${r.review_sequence_no} (${fmtDate(
                        r.review_scheduled_for,
                      )})`}
                    >
                      #{r.review_sequence_no}
                    </td>
                    <td className="px-3 py-2 max-w-[180px] truncate" title={r.client_name}>
                      <div className="truncate">{r.client_name || "—"}</div>
                      {r.branch_name && (
                        <div className="text-[10px] text-[var(--color-text-muted)] truncate">
                          {r.branch_name}
                        </div>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </TableShell>

          {/* ── paginacja ── */}
          <div className="flex flex-wrap items-center gap-2 mt-3 text-xs font-mono text-[var(--color-text-muted)]">
            <span>
              {data.count} {data.count === 1 ? "zadanie" : "zadań"} · strona {data.page} z{" "}
              {data.pages}
            </span>
            <div className="ml-auto flex items-center gap-2">
              <select
                className="input-base"
                value={pageSize}
                onChange={(e) => {
                  setPageSize(Number(e.target.value));
                  setPage(1);
                }}
                title="Wierszy na stronę"
              >
                {PAGE_SIZES.map((s) => (
                  <option key={s} value={s}>
                    {s} / str.
                  </option>
                ))}
              </select>
              <Button
                size="sm"
                variant="ghost"
                disabled={data.page <= 1 || list.isFetching}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
              >
                ‹ Poprzednia
              </Button>
              <Button
                size="sm"
                variant="ghost"
                disabled={data.page >= data.pages || list.isFetching}
                onClick={() => setPage((p) => p + 1)}
              >
                Następna ›
              </Button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
